import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useMemo, type FunctionComponent } from "react";
import { tabToNormalizeTo } from "../tabUrlNormalization";
import { useTranslation } from "react-i18next";
import {
  FiArrowLeft,
  FiBarChart2,
  FiCalendar,
  FiChevronRight,
  FiShield,
  FiTag,
} from "react-icons/fi";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  type BuyerBidV2DetailDto,
  type BuyerBidV2EspdDto,
  useGetEvaluationQuery,
  useListBidV2SForEventQuery,
  useGetBidV2ForBuyerQuery,
  useGetSourcingEventQuery,
  useListNegotiationRoundsV2Query,
} from "../../../../autogen/bff-api";
import { isTwoStageProcedure } from "../../../sourcing-events/buyer/wizard/utils/procedure";
import { isNotFoundError } from "../../../../common/errors/isNotFoundError";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { useSub } from "../../../../common/subscription/useSub";
import { urls } from "../../../../urls";
import { formatEstimatedValue } from "../../../sourcing-events/portal/utils/displayFormatters";
import { isAwardDecided } from "../bidOutcome";
import { BidV2StatusTag } from "../BidV2StatusTag";
import { ClarificationTab } from "./ClarificationTab";
import { DocumentsTab } from "./DocumentsTab";
import { ProductsTab } from "./ProductsTab";
import { canSelectWinner } from "../award/canSelectWinner";
import { supportsNegotiationRounds } from "../rounds/supportsNegotiationRounds";
import { BidDetailSidebar } from "./BidDetailSidebar";
import { DeclarationsTab } from "./DeclarationsTab";
import { AdmissionDeclarationNotice } from "./AdmissionDeclarationNotice";
import { EspdTab } from "./EspdTab";
import { HeaderStat } from "./HeaderStat";
import { OverviewTab } from "./OverviewTab";
import { AwardCriteriaTab } from "./AwardCriteriaTab";
import { MinimumRequirementsTab } from "./MinimumRequirementsTab";
import { QualificationsTab } from "./QualificationsTab";
import {
  deriveBidEvaluationSummary,
  formatRankLabel,
} from "./bidEvaluationSummary";
import { useNavLabel } from "../../../../common/layout/v2/useNavLabel";

const DASH = "–";

type BidDetailTab =
  | "overview"
  | "documents"
  | "products"
  | "qualifications"
  | "declarations"
  | "minimumRequirements"
  | "awardCriteria"
  | "espd"
  | "clarification";

type TabBody = FunctionComponent<{ detail: BuyerBidV2DetailDto }>;

const QualificationsTabAdapter = ({ detail }: { detail: BuyerBidV2DetailDto }) => (
  <QualificationsTab eventId={detail.bid.forEvent} bidId={detail.bid.id} detail={detail} />
);

const EspdTabAdapter = ({ detail }: { detail: BuyerBidV2DetailDto }) => {
  if (detail.espd) return <EspdTab espd={detail.espd} />;
  if (detail.admissionDeclaration) {
    return <AdmissionDeclarationNotice admission={detail.admissionDeclaration} />;
  }
  return null;
};

const DocumentsTabAdapter = ({ detail }: { detail: BuyerBidV2DetailDto }) => (
  <DocumentsTab documents={detail.bid.documents ?? []} signedDocumentId={detail.bid.signedDocumentId} />
);

const DeclarationsTabAdapter = ({ detail }: { detail: BuyerBidV2DetailDto }) =>
  detail.declarations ? (
    <DeclarationsTab declarations={detail.declarations} resetKey={detail.bid.id} />
  ) : null;

const MinimumRequirementsTabAdapter = ({ detail }: { detail: BuyerBidV2DetailDto }) => (
  <MinimumRequirementsTab eventId={detail.bid.forEvent} bidId={detail.bid.id} detail={detail} />
);

// Tab labels are rendered via `t(tab.labelKey)` below; list them as literal `t("…")` here
// so i18next-parser keeps the keys in the locale files (it can't see the data-driven call).
// t("Overview") t("Documents") t("Products") t("Qualification requirements") t("Self-declaration") t("Minimum requirements") t("Evaluation") t("Exclusion grounds") t("Clarification")
const TABS: { key: BidDetailTab; labelKey: string; Body: TabBody }[] = [
  { key: "overview", labelKey: "Overview", Body: OverviewTab },
  { key: "documents", labelKey: "Documents", Body: DocumentsTabAdapter },
  { key: "products", labelKey: "Products", Body: ProductsTab },
  {
    key: "qualifications",
    labelKey: "Qualification requirements",
    Body: QualificationsTabAdapter,
  },
  { key: "declarations", labelKey: "Self-declaration", Body: DeclarationsTabAdapter },
  {
    key: "minimumRequirements",
    labelKey: "Minimum requirements",
    Body: MinimumRequirementsTabAdapter,
  },
  // The tab is where the bid is scored against the award criteria, so it is named after the work,
  // not the input. The `awardCriteria` key stays: it is the ?tab= value links and tests address.
  { key: "awardCriteria", labelKey: "Evaluation", Body: AwardCriteriaTab },
  { key: "espd", labelKey: "Exclusion grounds", Body: EspdTabAdapter },
  { key: "clarification", labelKey: "Clarification", Body: ClarificationTab },
];

const DEFAULT_TAB: BidDetailTab = "overview";

const parseBidDetailTab = (value: string | null): BidDetailTab | null =>
  TABS.find((tab) => tab.key === value)?.key ?? null;

const formatPrice = (price: BuyerBidV2DetailDto["bid"]["price"]): string =>
  price?.value != null
    ? formatEstimatedValue(price.value, price.currency)
    : DASH;

const formatEspdHeader = (
  espd: BuyerBidV2EspdDto | null | undefined,
  t: (value: string, options?: { count: number }) => string,
) => {
  if (!espd?.hasResponse)
    return { value: DASH, tone: "default" as const, pending: true };
  if (espd.deviationCount > 0) {
    return {
      value: t("espd_deviation_count", { count: espd.deviationCount }),
      tone: "issue" as const,
      pending: false,
    };
  }
  if (espd.unansweredCount > 0) {
    return {
      value: t("espd_unanswered_count", { count: espd.unansweredCount }),
      tone: "neutral" as const,
      pending: false,
    };
  }
  return { value: t("OK"), tone: "ok" as const, pending: false };
};

export const BidDetailView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDateTime = useDisplayDateWithTime();
  const { eventId, bidId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = useSub();

  // Private procurements receive bids too, so this follows buyer access rather than the
  // public-procurement flag (see SourcingEventsList).
  if (!sub.hasBuyerAccess) {
    return (
      <Center py="20">
        <Text color="smMuted">
          {t("Your organization does not have access to this feature.")}
        </Text>
      </Center>
    );
  }

  if (!eventId || !bidId) {
    return (
      <NotFound
        title={t("This link is not valid.")}
        backUrl={urls.v2.sourcingEvents.viewAll}
        backLabel={t("Back to procurements")}
        onNavigate={navigate}
      />
    );
  }

  return (
    <BidDetailPage
      eventId={eventId}
      bidId={bidId}
      requestedTab={searchParams.get("tab")}
      onTabChange={(tab) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", tab);
        setSearchParams(next, { replace: true });
      }}
      navigate={navigate}
      displayDateTime={displayDateTime}
    />
  );
};

type Navigate = ReturnType<typeof useNavigate>;

const BidDetailPage = ({
  eventId,
  bidId,
  requestedTab,
  onTabChange,
  navigate,
  displayDateTime,
}: {
  eventId: string;
  bidId: string;
  requestedTab: string | null;
  onTabChange: (tab: BidDetailTab) => void;
  navigate: Navigate;
  displayDateTime: (value: string) => string;
}) => {
  const { t } = useTranslation();
  const navLabel = useNavLabel();
  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useGetBidV2ForBuyerQuery({ eventId, bidId });
  const { data: event, isLoading: eventIsLoading } = useGetSourcingEventQuery({ eventId });
  const twoStage = isTwoStageProcedure(event?.eventContext?.type === "Public" ? event.eventContext.procedureType : undefined);
  // The bid's rank lives in ITS stage's evaluation (a round bid ranks within the round).
  const { data: bidsList } = useListBidV2SForEventQuery({ eventId });
  const bidStageRound = bidsList?.bids.find((candidate) => candidate.id === bidId)?.forNegotiationRound;
  const evaluation = useGetEvaluationQuery({ eventId, round: bidStageRound ?? undefined });
  const awarded = isAwardDecided(evaluation.data?.bids);
  // Same rule as the award tab this button navigates to — rounds included, since a negotiated
  // competition finishes its evaluation by completing them rather than by leaving EVALUATION.
  const { data: roundsData } = useListNegotiationRoundsV2Query(
    event && supportsNegotiationRounds(event) ? { eventId } : skipToken,
  );
  const winnerSelectable = event ? canSelectWinner(event.status, roundsData?.rounds ?? []) : false;
  const bidsTabUrl = `${urls.v2.sourcingEvents.view.go(eventId)}?tab=bids`;

  const summary = useMemo(
    () =>
      deriveBidEvaluationSummary(evaluation.data, detail?.bid.id ?? "", {
        isLoading: evaluation.isLoading,
        isError: evaluation.isError,
      }),
    [evaluation.data, evaluation.isLoading, evaluation.isError, detail?.bid.id],
  );

  const visibleTabs = useMemo(
    () =>
      detail == null
        ? []
        : TABS.filter((tab) => !(twoStage && (tab.key === "qualifications" || tab.key === "espd")))
            .filter((tab) => tab.key !== "products" || (detail.bid.products?.length ?? 0) > 0)
            .filter((tab) => tab.key !== "espd" || !!detail.espd || !!detail.admissionDeclaration)
            .filter((tab) => tab.key !== "declarations" || !!detail.declarations)
            .filter((tab) => tab.key !== "qualifications" || (detail.selectionCriteria?.length ?? 0) > 0)
            .filter(
              (tab) => tab.key !== "minimumRequirements" || (detail.minimumRequirements?.length ?? 0) > 0,
            )
            .filter((tab) => tab.key !== "awardCriteria" || (detail.awardCriteria?.length ?? 0) > 0),
    [detail, event, twoStage],
  );
  const normalizeTo =
    detail == null || eventIsLoading
      ? null
      : tabToNormalizeTo(
          requestedTab,
          parseBidDetailTab(requestedTab),
          visibleTabs.map((tab) => tab.key),
          DEFAULT_TAB,
        );
  const resolvedActiveTab = normalizeTo ?? parseBidDetailTab(requestedTab) ?? DEFAULT_TAB;
  useEffect(() => {
    if (normalizeTo != null) onTabChange(normalizeTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizeTo]);

  if (isLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  if (isError || !detail) {
    const notFound = isError && isNotFoundError(error);
    return (
      <NotFound
        title={
          notFound
            ? t("The bid could not be found.")
            : t("Could not load the bid.")
        }
        backUrl={bidsTabUrl}
        backLabel={t("Back to bids")}
        onNavigate={navigate}
      />
    );
  }

  const { bid } = detail;
  const supplierName = detail.ownerName || t("Unknown");
  const activeIndex = Math.max(
    0,
    visibleTabs.findIndex((tab) => tab.key === resolvedActiveTab),
  );
  const espdHeader = formatEspdHeader(detail.espd, t);

  return (
    <Box>
      <Grid
        templateColumns={{ base: "1fr", xl: "minmax(0, 1fr) 340px" }}
        gap="6"
        alignItems="start"
      >
        <GridItem
          minW="0"
          gridColumn={{ base: "1", xl: "1" }}
          gridRow={{ base: "1", xl: "1" }}
        >
          <Box
            bg="smBackground"
            rounded="xl"
            border="1px solid"
            borderColor="smBorder"
            p={{ base: "4", md: "6" }}
          >
            <Breadcrumb
              separator={
                <Icon as={FiChevronRight} color="smMuted" boxSize="3" />
              }
              fontSize="sm"
              mb="3"
              sx={{
                ol: {
                  flexWrap: "wrap",
                  rowGap: 1,
                },
              }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to={urls.v2.sourcingEvents.viewAll}
                  color="smPrimary"
                >
                  {navLabel("All procurements")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to={urls.v2.sourcingEvents.view.go(eventId)}
                  color="smPrimary"
                  isTruncated
                  maxW={{ base: "180px", md: "280px" }}
                >
                  {detail.eventTitle || t("Untitled")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to={bidsTabUrl}
                  color="smPrimary"
                >
                  {t("Bids")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  color="smMuted"
                  isTruncated
                  maxW={{ base: "180px", md: "280px" }}
                >
                  {supplierName}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            <HStack spacing="3" align="center" flexWrap="wrap" mb="1">
              <Heading as="h1" size="lg" color="smText">
                {supplierName}
              </Heading>
              <BidV2StatusTag
                status={bid.status}
                awarded={awarded}
                size="md"
              />
            </HStack>
            {detail.ownerOrganizationNumber && (
              <Text fontSize="sm" color="smMuted" fontFamily="mono" mb="5">
                {`${t("Organization number")}: ${detail.ownerOrganizationNumber}`}
              </Text>
            )}

            <SimpleGrid minChildWidth="200px" spacing="3">
              <HeaderStat
                icon={FiTag}
                label={t("Total price")}
                value={formatPrice(bid.price)}
                tone="primary"
                mono
              />
              <HeaderStat
                icon={FiCalendar}
                label={t("Submitted")}
                value={
                  bid.submittedAt ? displayDateTime(bid.submittedAt) : DASH
                }
              />
              <HeaderStat
                icon={FiBarChart2}
                label={t("Ranking")}
                value={formatRankLabel(summary, t)}
                mono
                pending={summary.state === "loading"}
              />
              <HeaderStat
                icon={FiShield}
                label={t("Exclusion grounds")}
                value={espdHeader.value}
                tone={espdHeader.tone}
                pending={espdHeader.pending}
                onClick={
                  twoStage || resolvedActiveTab === "espd"
                    ? undefined
                    : () => onTabChange("espd")
                }
              />
            </SimpleGrid>
          </Box>
        </GridItem>

        <GridItem
          gridColumn={{ base: "1", xl: "2" }}
          gridRow={{ base: "2", xl: "1" }}
          h={{ xl: "0" }}
          overflow={{ xl: "visible" }}
        >
          <Box position={{ xl: "sticky" }} top={{ xl: "0" }}>
            <BidDetailSidebar
              detail={detail}
              summary={summary}
              awarded={awarded}
              canSelectWinner={winnerSelectable}
              onMarkWinner={() =>
                navigate(
                  `${urls.v2.sourcingEvents.view.go(eventId)}?tab=award&bid=${detail.bid.id}`,
                )
              }
              onOpenRejectionDetails={() => {
                onTabChange("overview");
                window.requestAnimationFrame(() => {
                  document
                    .getElementById("bid-rejection-details")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }}
            />
          </Box>
        </GridItem>

        <GridItem
          minW="0"
          gridColumn={{ base: "1", xl: "1" }}
          gridRow={{ base: "3", xl: "2" }}
        >
          <Tabs
            index={activeIndex}
            onChange={(i) => {
              const next = visibleTabs[i].key;
              if (next !== resolvedActiveTab) onTabChange(next);
            }}
            colorScheme="teal"
            isLazy
            lazyBehavior="keepMounted"
          >
            <TabList overflowX="auto" overflowY="hidden">
              {visibleTabs.map((tab) => (
                <Tab key={tab.key} whiteSpace="nowrap">
                  {t(tab.labelKey)}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {visibleTabs.map((tab) => (
                <TabPanel key={tab.key} px="0">
                  <tab.Body detail={detail} />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </GridItem>
      </Grid>
    </Box>
  );
};

const NotFound = ({
  title,
  backUrl,
  backLabel,
  onNavigate,
}: {
  title: string;
  backUrl: string;
  backLabel: string;
  onNavigate: Navigate;
}) => (
  <Center py="20">
    <Flex direction="column" align="center" gap="4">
      <Text color="smMuted">{title}</Text>
      <Button
        variant="outline"
        size="sm"
        leftIcon={<Icon as={FiArrowLeft} />}
        onClick={() => onNavigate(backUrl)}
      >
        {backLabel}
      </Button>
    </Flex>
  </Center>
);
